import Element, { ElementParams } from './element';
import Ship from './ship/ship';
import Weapon from './ship/weapon';
import AttackMoveToOrder from '../order/attackMoveToOrder';
import PatrolOrder from '../order/patrolOrder';

import { getAngleBy2XY } from '../../utils/geometry';

export interface ProjectileParams extends ElementParams {
  ship: Ship;
  damage: number;
  range: number;
  seeker?: boolean;
  target?: Element;
  weapon?: Weapon;
}

export default class Projectile extends Element implements ProjectileParams {
  ship!: Ship;
  damage = 1;
  range: number = 10; // unit: pixels
  seeker? = false;
  target?: Element;
  weapon?: Weapon;

  /* override default values */
  moveSpeed = 600;
  width = 5;
  height = 30;

  distanceTraveled = 0; // unit: pixels

  constructor(params: ProjectileParams) {
    super(params);
    this.applyParams(params);
  }

  update(modifier: number): Projectile {
    if (this.distanceTraveled >= this.range) {
      this.remove();
      return this;
    }

    if (this.seeker) {
      if (!this.target)
        this.target = this.getNearestEnemy();
      if (this.target) {
        const angleBetweenMeAndTarget = getAngleBy2XY({
          x1: this.x,
          y1: this.y,
          x2: this.target.x,
          y2: this.target.y,
        });
        this.rotateTowardsAngle(angleBetweenMeAndTarget, modifier);
        this.moveAngle = this.lookAngle;
      }
    }

    this.move(modifier);
    this.distanceTraveled += this.moveSpeed * modifier;
    this.updateQuadtree();
    this.handleCollision();
    return this;
  }

  getNearestEnemy(): Element | undefined {
    return this.getNearestElement(
      this.getElementsInRange(
        this.getTargetsQuadtree({ includeHazards: true }),
        this.range
      )
    );
  }

  handleCollision(): void {
    const targetInCollision = this.getTargetsInCollision()[0] as Ship;
    if (targetInCollision) {
      targetInCollision.takeDamages(this.damage);
      this.remove();

      const { behaviour } = targetInCollision;

      /* idle unit will attack-move toward projectile that hit him */
      if (
        targetInCollision.HP > 0
        && behaviour
        && this.weapon?.ship
        && (behaviour.orders.length === 0 || behaviour.orders[0] instanceof PatrolOrder)
      ) {
        const { x, y } = this.weapon.ship;
        if (behaviour.orders.length === 0 && !targetInCollision.isManuallyControlled) {
          behaviour.unshiftOrder(new AttackMoveToOrder({
            x,
            y,
          }));
        }

      }
    }
  }
}
